/**
 * Owned Media — formulario schema-driven para las 16 entidades (§5.2). El
 * schema (shared/types/oml.ts) dice qué campos hay y de qué tipo; acá se dibuja
 * el input que corresponde y se aplican las reglas de UX del doc:
 *  - edición por bloque con formulario (§7.1); botón solo para quien puede (§8.2);
 *  - restringidos con candado (§9.3) y campos que este rol no edita, deshabilitados;
 *  - guardado por campo: viajan solo los tocados + la versión por campo leída;
 *    conflicto ⇒ se muestra quién cambió qué y se elige (§13.4);
 *  - borrador en el servidor cada 30 s y al salir; al volver se ofrece recuperar (§13.4);
 *  - justificación obligatoria cuando el cambio la exige (§8.5);
 *  - "¿Estás seguro de que esto no es una clave?" con confirmación explícita (§13.3, AC-10);
 *  - errores del backend campo a campo (código estable + mensaje del doc).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Eye, Pencil, UserRound } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelectPopover } from '@/components/ui/multi-select-popover';
import { MemberPickerDialog } from '@/components/governance/MemberPickerDialog';
import { MarkdownText } from '@/components/performance/MarkdownText';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { useOmlMutations, type OmlAccountDetail, type OmlConfig } from '@/hooks/useOml';
import { isDesignAssignment, justificationRequired, sameValue, withDefaults, type OmlIssue, type Values } from '../../../../shared/oml-rules';
import { OML_SCHEMA, type OmlChildType, type OmlEntityType, type OmlFieldDef } from '../../../../shared/types/oml';
import { AssetField } from './AssetField';
import { LockedField } from './LockedField';
import { entityLabel, entityName, fieldHelp, fieldLabel, omlErrorCode, omlErrorData } from './oml-ui';

export interface EntityFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: OmlEntityType;
  accountId: string;
  /** Registro existente (edición). Ausente = alta. */
  entity?: Values & { id?: string; locked?: string[]; fieldVersions?: Record<string, string>; updatedAt?: string };
  /** Detalle de la cuenta (hijas para relaciones, acceso). Ausente solo al crear una cuenta. */
  detail?: OmlAccountDetail | null;
  config: OmlConfig | undefined;
  members: { email: string; name: string; role?: string; area?: string; team?: string; status?: string }[];
  /** Alta de cuenta: plantilla por tipo de servicio (§11.2). */
  templateId?: string;
  onSaved?: (item: Values) => void;
  /**
   * Edición rápida (lápiz al lado de un valor, OML 08/10/2026): muestra solo
   * estos campos. Conflictos, justificación, secretos y borrador siguen igual.
   */
  onlyFields?: string[];
}

type Conflict = { field: string; by: string; at: string; current: unknown };

const PRECISION_DAY = 'day';
const PRECISION_MONTH = 'month';

export function EntityForm({ open, onOpenChange, type, accountId, entity, detail, config, members, templateId, onSaved, onlyFields }: EntityFormProps) {
  const { t } = useTranslation();
  const def = OML_SCHEMA[type];
  const isEdit = !!entity?.id;
  const m = useOmlMutations();
  const locked = useMemo(() => new Set(entity?.locked ?? []), [entity]);
  const acc = detail?.access;
  const entityKey = `${type}#${accountId}#${entity?.id ?? 'new'}`;

  const [values, setValues] = useState<Values>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [baseVersions, setBaseVersions] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, OmlIssue>>({});
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [justification, setJustification] = useState('');
  const [confirmSecret, setConfirmSecret] = useState(false);
  const [preview, setPreview] = useState<Record<string, boolean>>({});
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [draftOffer, setDraftOffer] = useState<{ values: Values; savedAt: string } | null>(null);
  const dirtyRef = useRef(false);

  const draftQ = trpc.oml.views.getDraft.useQuery({ entityKey }, { enabled: open, staleTime: 0, retry: false });

  // Estado inicial al abrir
  useEffect(() => {
    if (!open) return;
    const base = withDefaults(type, entity ? { ...entity } : {});
    delete base.locked;
    setValues(base);
    setTouched(new Set());
    setBaseVersions({ ...(entity?.fieldVersions ?? {}) });
    setErrors({});
    setConflicts([]);
    setJustification('');
    setConfirmSecret(false);
    setDraftOffer(null);
    dirtyRef.current = false;
  }, [open, type, entity]);

  // Borrador guardado en el servidor (§13.4): se ofrece si es más nuevo que el registro.
  useEffect(() => {
    if (!open || !draftQ.data) return;
    const d = draftQ.data;
    if (!entity?.updatedAt || d.savedAt > entity.updatedAt) setDraftOffer({ values: d.values as Values, savedAt: d.savedAt });
  }, [open, draftQ.data, entity?.updatedAt]);

  const saveDraft = useCallback(() => {
    if (!dirtyRef.current) return;
    m.putDraft.mutate({ entityKey, values });
    dirtyRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityKey, values]);

  useEffect(() => {
    if (!open) return;
    const id = setInterval(saveDraft, 30_000);
    return () => clearInterval(id);
  }, [open, saveDraft]);

  const close = (o: boolean) => {
    if (!o && dirtyRef.current) saveDraft();
    onOpenChange(o);
  };

  const set = (key: string, v: unknown) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    setTouched((prev) => new Set(prev).add(key));
    dirtyRef.current = true;
    setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const children = detail?.children;
  const relationOptions = (f: OmlFieldDef): { value: string; label: string }[] => {
    const rel = f.relation;
    if (!rel || !children) return [];
    let rows = (children[rel] ?? []) as Values[];
    if (f.key === 'freelancerAssignmentId') rows = rows.filter((r) => isDesignAssignment(r as never));
    if (rel === 'segment') rows = rows.filter((r) => r.active !== false);
    if (rel === 'brand') rows = rows.filter((r) => r.active !== false);
    return rows.map((r) => ({ value: String(r.id), label: entityName(rel, r) }));
  };

  const memberName = (email: unknown) => members.find((x) => x.email === String(email ?? '').toLowerCase())?.name ?? (email ? String(email) : '');

  const canEditField = (f: OmlFieldDef): boolean => {
    if (f.system) return false;
    if (locked.has(f.key)) return false;
    if (type === 'account' && acc) {
      if (acc.editableAccountFields === 'none') return false;
      if (acc.editableAccountFields !== 'all') return acc.editableAccountFields.includes(f.key);
      if (f.key === 'capacitySummary' && !(acc.roles.includes('lead') || acc.roles.includes('admin'))) return false;
      if (f.restricted === 'commercial' && !acc.canSeeCommercial) return false;
    }
    if (type === 'assignment' && f.key === 'dedication' && acc && !(acc.roles.includes('lead') || acc.roles.includes('admin'))) return false;
    if (type === 'experiment' && f.key === 'primaryMetric' && entity?.status === 'Corriendo') return false;
    return true;
  };

  const visible = (f: OmlFieldDef): boolean => {
    if (f.key === 'growthPartners') return false;
    if (onlyFields && !onlyFields.includes(f.key)) return false;
    // Al crear la cuenta todavía no hay id para colgar adjuntos (frontend-auditor): el logo se sube al editar.
    if (accountId === 'new' && (f.type === 'file' || f.type === 'files')) return false;
    if (f.system) return isEdit && values[f.key] !== undefined && values[f.key] !== null && values[f.key] !== '';
    if (type === 'assignment') {
      if (f.key === 'email' && values.personType !== 'Winclap') return false;
      if (f.key === 'externalName' && values.personType !== 'Freelance externo') return false;
    }
    if (type === 'experiment') {
      if (f.key === 'controlGroupLevel' && values.hasControlGroup === false) return false;
      if (f.key === 'noControlReason' && values.hasControlGroup !== false) return false;
      if (['result', 'reachedSignificance', 'noEffectDiagnosis', 'decision', 'learning'].includes(f.key) && !isEdit) return false;
    }
    if (type === 'journey' && f.key === 'discardReason' && values.status !== 'Descartado') return false;
    if (type === 'experiment' && f.key === 'discardReason' && values.status !== 'Descartado') return false;
    if (type === 'project' && f.key === 'briefMissing' && values.briefComplete === true) return false;
    if (type === 'project' && f.key === 'assetsReceivedAt' && values.assetsReceived !== true) return false;
    if (type === 'stakeholder' && ['organization', 'organizationType'].includes(f.key) && values.stakeholderType === 'Winclap') return false;
    if (type === 'link' && f.key === 'platformAccessId' && values.requiresSpecialAccess !== true) return false;
    return true;
  };

  const isRequired = (f: OmlFieldDef): boolean => {
    if (f.required) return true;
    if (f.requiredIf && sameValue(values[f.requiredIf.field], f.requiredIf.equals)) return true;
    if (f.requiredIfPresent && values[f.requiredIfPresent] !== undefined && values[f.requiredIfPresent] !== null && values[f.requiredIfPresent] !== '') return true;
    return false;
  };

  const needsJustification = useMemo(() => justificationRequired(type, isEdit ? (entity as Values) : null, values), [type, isEdit, entity, values]);
  // Un error de un campo que no está a la vista (edición rápida) se muestra arriba, no se pierde.
  const generalErrors = Object.values(errors).filter((e) => { const f = def.fields.find((x) => x.key === e.field); return !f || !visible(f); });
  const secretIssue = errors.credentialLocation?.code === 'secret_warning';

  const submit = async () => {
    setErrors({});
    setConflicts([]);
    const sendable = (f: OmlFieldDef) => visible(f) && !f.system && !locked.has(f.key) && canEditField(f);
    try {
      let saved: Values;
      let warnings: OmlIssue[] = [];
      if (type === 'account') {
        if (isEdit) {
          const patch = Object.fromEntries([...touched].filter((k) => def.fields.some((f) => f.key === k && sendable(f))).map((k) => [k, values[k]]));
          const r = await m.updateAccount.mutateAsync({ accountId, patch, touched: Object.keys(patch), baseFieldVersions: baseVersions, justification: justification || undefined });
          saved = r.account as Values; warnings = r.warnings as OmlIssue[];
        } else {
          const vals = Object.fromEntries(def.fields.filter(sendable).map((f) => [f.key, values[f.key]]));
          const r = await m.createAccount.mutateAsync({ values: vals, templateId });
          saved = r.account as Values; warnings = r.warnings as OmlIssue[];
        }
      } else if (isEdit) {
        const patch = Object.fromEntries([...touched].filter((k) => def.fields.some((f) => f.key === k && sendable(f))).map((k) => [k, values[k]]));
        const r = await m.updateEntity.mutateAsync({ accountId, type: type as OmlChildType, id: String(entity!.id), patch, touched: Object.keys(patch), baseFieldVersions: baseVersions, justification: justification || undefined, confirmSecretWarning: confirmSecret || undefined });
        saved = r.item as Values; warnings = r.warnings as OmlIssue[];
      } else {
        const vals = Object.fromEntries(def.fields.filter(sendable).map((f) => [f.key, values[f.key]]));
        const r = await m.createEntity.mutateAsync({ accountId, type: type as OmlChildType, values: vals, justification: justification || undefined, confirmSecretWarning: confirmSecret || undefined });
        saved = r.item as Values; warnings = r.warnings as OmlIssue[];
      }
      for (const w of warnings) toast.info(w.message);
      m.deleteDraft.mutate({ entityKey });
      dirtyRef.current = false;
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      const data = omlErrorData(err);
      const code = omlErrorCode(err);
      if (code === 'CONFLICT' && data?.conflicts?.length) {
        setConflicts(data.conflicts);
        return;
      }
      if (data?.issues?.length) {
        setErrors(Object.fromEntries(data.issues.map((i) => [i.field, i])));
        return;
      }
      toast.error(err instanceof Error ? err.message : t('common.toast.error_al_guardar', 'Error al guardar'));
    }
  };

  const resolveConflict = (c: Conflict, useTheirs: boolean) => {
    if (useTheirs) {
      setValues((prev) => ({ ...prev, [c.field]: c.current }));
      setTouched((prev) => { const n = new Set(prev); n.delete(c.field); return n; });
    }
    setBaseVersions((prev) => ({ ...prev, [c.field]: c.at }));
    setConflicts((prev) => prev.filter((x) => x.field !== c.field));
  };

  const renderInput = (f: OmlFieldDef) => {
    const v = values[f.key];
    const disabled = !canEditField(f);
    const id = `oml-${type}-${f.key}`;
    const list = f.list ? config?.catalogs[f.list] ?? [] : [];
    switch (f.type) {
      case 'text':
      case 'link':
        return <Input id={id} value={String(v ?? '')} disabled={disabled} type={f.type === 'link' ? 'url' : 'text'} placeholder={f.type === 'link' ? 'https://' : undefined} onChange={(e) => set(f.key, e.target.value)} />;
      case 'richtext':
        return (
          <div className="space-y-1">
            <div className="flex justify-end">
              <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setPreview((p) => ({ ...p, [f.key]: !p[f.key] }))}>
                {preview[f.key] ? <><Pencil className="h-3 w-3 mr-1" />{t('oml.form.edit', 'Editar')}</> : <><Eye className="h-3 w-3 mr-1" />{t('oml.form.preview', 'Vista previa')}</>}
              </Button>
            </div>
            {preview[f.key]
              ? <div className="rounded-md border p-3 min-h-[80px]"><MarkdownText>{String(v ?? '')}</MarkdownText></div>
              : <Textarea id={id} value={String(v ?? '')} disabled={disabled} rows={5} onChange={(e) => set(f.key, e.target.value)} placeholder={t('oml.form.markdown_ph', 'Admite listas, tablas y negritas (Markdown)')} />}
          </div>
        );
      case 'number':
      case 'percent':
        return (
          <div className="flex items-center gap-2">
            <Input id={id} type="number" inputMode="decimal" step={f.type === 'percent' || !f.integer ? '0.01' : '1'} min={f.min} max={f.max} value={v === null || v === undefined ? '' : String(v)} disabled={disabled}
              onChange={(e) => set(f.key, e.target.value === '' ? null : Number(e.target.value))} className="max-w-[220px]" />
            {f.type === 'percent' && <span className="text-sm text-muted-foreground">%</span>}
          </div>
        );
      case 'date':
        return <Input id={id} type="date" value={String(v ?? '').slice(0, 10)} disabled={disabled} onChange={(e) => set(f.key, e.target.value || null)} className="max-w-[220px]" />;
      case 'dateMonth': {
        const s = String(v ?? '');
        const precision = s.length === 10 ? PRECISION_DAY : PRECISION_MONTH;
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={precision} disabled={disabled} onValueChange={(p) => set(f.key, p === PRECISION_DAY ? (s ? `${s.slice(0, 7)}-01` : '') : s.slice(0, 7))}>
              <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={PRECISION_MONTH}>{t('oml.form.precision_month', 'Mes y año')}</SelectItem>
                <SelectItem value={PRECISION_DAY}>{t('oml.form.precision_day', 'Día exacto')}</SelectItem>
              </SelectContent>
            </Select>
            <Input id={id} type={precision === PRECISION_DAY ? 'date' : 'month'} value={precision === PRECISION_DAY ? s : s.slice(0, 7)} disabled={disabled} onChange={(e) => set(f.key, e.target.value || null)} className="max-w-[200px]" />
          </div>
        );
      }
      case 'bool':
        return <div className="flex items-center gap-2"><Switch id={id} checked={v === true} disabled={disabled} onCheckedChange={(c) => set(f.key, c)} /><span className="text-sm text-muted-foreground">{v === true ? t('common.yes', 'Sí') : t('common.no', 'No')}</span></div>;
      case 'select':
        return (
          <Select value={v ? String(v) : ''} disabled={disabled} onValueChange={(x) => set(f.key, x === '__none__' ? null : x)}>
            <SelectTrigger id={id}><SelectValue placeholder={t('oml.form.choose', 'Elegir…')} /></SelectTrigger>
            <SelectContent>
              {!isRequired(f) && <SelectItem value="__none__">—</SelectItem>}
              {list.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      case 'multiselect':
        return <MultiSelectPopover label={fieldLabel(t, type, f.key)} options={list.map((o) => ({ value: o }))} selected={Array.isArray(v) ? (v as string[]) : []} onChange={(next) => set(f.key, next)} allLabel={t('oml.form.none_selected', 'Ninguno')} triggerClassName={cn('w-full justify-between', disabled && 'pointer-events-none opacity-60')} />;
      case 'relation': {
        const opts = relationOptions(f);
        return (
          <Select value={v ? String(v) : ''} disabled={disabled} onValueChange={(x) => set(f.key, x === '__none__' ? null : x)}>
            <SelectTrigger id={id}><SelectValue placeholder={opts.length ? t('oml.form.choose', 'Elegir…') : t('oml.form.no_options', 'Todavía no hay registros para elegir')} /></SelectTrigger>
            <SelectContent>
              {!isRequired(f) && <SelectItem value="__none__">—</SelectItem>}
              {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      }
      case 'relations': {
        const opts = relationOptions(f);
        return <MultiSelectPopover label={fieldLabel(t, type, f.key)} options={opts} selected={Array.isArray(v) ? (v as string[]) : []} onChange={(next) => set(f.key, next)} allLabel={t('oml.form.none_selected', 'Ninguno')} triggerClassName={cn('w-full justify-between', disabled && 'pointer-events-none opacity-60')} />;
      }
      case 'user':
        return (
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" disabled={disabled} onClick={() => setPickerFor(f.key)} className="justify-start font-normal min-w-[240px]">
              <UserRound className="h-4 w-4 mr-2 text-muted-foreground" />{v ? memberName(v) : t('oml.form.pick_person', 'Elegir persona')}
            </Button>
            {!!v && !disabled && !isRequired(f) && <Button type="button" variant="ghost" size="sm" onClick={() => set(f.key, null)}>{t('common.remove', 'Quitar')}</Button>}
          </div>
        );
      case 'file':
        return <AssetField accountId={accountId} value={v ? [String(v)] : []} multiple={false} disabled={disabled} onChange={(ids) => set(f.key, ids[0] ?? null)} />;
      case 'files':
        return <AssetField accountId={accountId} value={Array.isArray(v) ? (v as string[]) : []} multiple disabled={disabled} onChange={(ids) => set(f.key, ids)} />;
      default:
        return null;
    }
  };

  const busy = m.createAccount.isPending || m.updateAccount.isPending || m.createEntity.isPending || m.updateEntity.isPending;
  const title = isEdit ? t('oml.form.edit_title', 'Editar {{entity}}', { entity: entityLabel(t, type).toLowerCase() }) : t('oml.form.new_title', 'Nuevo: {{entity}}', { entity: entityLabel(t, type) });

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{isEdit ? t('oml.form.edit_desc', 'Solo se guardan los campos que toques. Cada cambio queda en el historial con tu nombre.') : t('oml.form.new_desc', 'Se publica directo: no hay aprobación de contenido. Lo que marca * es requerido.')}</DialogDescription>
        </DialogHeader>

        {draftOffer && (
          <Alert>
            <AlertTitle>{t('oml.form.draft_title', 'Tenés un borrador sin guardar')}</AlertTitle>
            <AlertDescription className="flex flex-wrap items-center gap-2">
              <span>{t('oml.form.draft_desc', 'Guardado el {{when}}. ¿Lo recuperamos?', { when: new Date(draftOffer.savedAt).toLocaleString() })}</span>
              <Button size="sm" onClick={() => { setValues((p) => ({ ...p, ...draftOffer.values })); setTouched(new Set(Object.keys(draftOffer.values))); dirtyRef.current = true; setDraftOffer(null); }}>{t('oml.form.draft_recover', 'Recuperar')}</Button>
              <Button size="sm" variant="outline" disabled={m.deleteDraft.isPending} onClick={() => { m.deleteDraft.mutate({ entityKey }); setDraftOffer(null); }}>{t('oml.form.draft_discard', 'Descartar')}</Button>
            </AlertDescription>
          </Alert>
        )}

        {conflicts.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('oml.form.conflict_title', 'Alguien cambió este registro mientras lo editabas')}</AlertTitle>
            <AlertDescription>
              <ul className="space-y-2 mt-1">
                {conflicts.map((c) => (
                  <li key={c.field} className="flex flex-wrap items-center gap-2 text-sm">
                    <span><strong>{c.field === '*' ? t('oml.form.whole_record', 'todo el registro') : fieldLabel(t, type, c.field)}</strong> · {memberName(c.by)} · {new Date(c.at).toLocaleString()}</span>
                    {c.field !== '*' && <Button size="sm" variant="outline" onClick={() => resolveConflict(c, true)}>{t('oml.form.use_theirs', 'Usar el valor nuevo')}</Button>}
                    <Button size="sm" variant="ghost" onClick={() => resolveConflict(c, false)}>{t('oml.form.keep_mine', 'Mantener el mío')}</Button>
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {generalErrors.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription><ul className="list-disc pl-4">{generalErrors.map((e) => <li key={e.field}>{e.message}</li>)}</ul></AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {def.fields.filter(visible).map((f) => {
            const err = errors[f.key];
            const wide = ['richtext', 'multiselect', 'relations', 'files'].includes(f.type) || f.key === 'name';
            const label = fieldLabel(t, type, f.key);
            const help = fieldHelp(t, type, f);
            return (
              <div key={f.key} className={cn('space-y-1.5', wide && 'md:col-span-2')}>
                <Label htmlFor={`oml-${type}-${f.key}`} className="flex items-center gap-1">
                  {label}{isRequired(f) && <span className="text-destructive">*</span>}
                  {f.restricted && <span className="text-[10px] uppercase tracking-wide text-amber-700 ml-1">{t('oml.form.restricted_tag', 'restringido')}</span>}
                </Label>
                {locked.has(f.key)
                  ? <LockedField label={label} accountId={accountId} field={f.key} requestable={f.restricted !== 'credential'} compact />
                  : f.system
                    ? <div className="text-sm rounded-md border bg-muted/40 px-3 py-2">{String(values[f.key] ?? '—')} <span className="text-xs text-muted-foreground">· {t('oml.form.system_field', 'lo calcula el sistema')}</span></div>
                    : renderInput(f)}
                {type === 'campaign' && f.key === 'name' && detail && <NamingHint accountId={accountId} />}
                {help && !err && <p className="text-xs text-muted-foreground">{help}</p>}
                {err && !(f.key === 'credentialLocation' && secretIssue) && <p className="text-xs text-destructive">{err.message}</p>}
                {f.key === 'credentialLocation' && secretIssue && (
                  <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/30">
                    <AlertTriangle className="h-4 w-4 text-amber-700" />
                    <AlertTitle>{t('oml.form.secret_title', '¿Estás seguro de que esto no es una clave/secreto?')}</AlertTitle>
                    <AlertDescription className="space-y-2">
                      <p>{t('oml.form.secret_desc', 'Esto podría ser una contraseña. Guardá solo la referencia al gestor (por ejemplo "1Password · bóveda OM-Laika"). Si es solo una referencia, confirmalo: queda registrado en el historial para que el Lead OMC lo revise.')}</p>
                      <label className="flex items-center gap-2 text-sm"><Checkbox checked={confirmSecret} onCheckedChange={(c) => setConfirmSecret(c === true)} />{t('oml.form.secret_confirm', 'Confirmo que es solo una referencia, no una clave')}</label>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            );
          })}
        </div>

        {needsJustification.required && (
          <div className="space-y-1.5 rounded-md border border-amber-300/70 bg-amber-50/60 dark:bg-amber-950/20 p-3">
            <Label htmlFor="oml-justification">{t('oml.form.justification', 'Justificación (obligatoria)')} <span className="text-destructive">*</span></Label>
            <p className="text-xs text-muted-foreground">{t('oml.form.justification_help', 'Este cambio la exige (deal, topes o capacity; acceso por cuenta compartida o personal; cierre de cuenta). Queda en el historial.')}</p>
            <Textarea id="oml-justification" value={justification} onChange={(e) => setJustification(e.target.value)} rows={2} />
            {errors.justification && <p className="text-xs text-destructive">{errors.justification.message}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>{t('common.cancel', 'Cancelar')}</Button>
          <Button onClick={submit} disabled={busy || (isEdit && touched.size === 0) || (needsJustification.required && justification.trim().length < 3)}>
            {busy ? t('common.saving', 'Guardando…') : t('common.save', 'Guardar')}
          </Button>
        </DialogFooter>

        {pickerFor && (
          <MemberPickerDialog
            open onOpenChange={(o) => { if (!o) setPickerFor(null); }}
            title={t('oml.form.pick_person', 'Elegir persona')}
            searchPlaceholder={t('oml.form.search_person', 'Buscar por nombre, mail o área')}
            noResultsText={t('oml.form.no_results', 'Sin resultados')}
            members={members}
            exclude={new Set()}
            onPick={(mem) => { set(pickerFor, mem.email); setPickerFor(null); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Convención de naming de la cuenta al lado del nombre de campaña (§5.2): se muestra, no se valida. */
function NamingHint({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const { data } = trpc.oml.accounts.namingConvention.useQuery({ accountId }, { staleTime: 60_000 });
  if (!data?.content) return null;
  return (
    <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs">
      <div className="font-medium mb-1">{t('oml.form.naming_hint', 'Convención de naming de esta cuenta')}</div>
      <MarkdownText className="text-xs">{data.content}</MarkdownText>
    </div>
  );
}
